import React, { useEffect, useState } from 'react';
import { verifier, ouvrirSession, sessionValide, attenteSecondes, ESSAIS_AVANT_ATTENTE } from '../verrou.js';

const CLE = 'corridorfx.session';

/**
 * Écran de déverrouillage. Toute l'application est derrière.
 *
 * La session vit dans `localStorage` et non dans IndexedDB : le verrou doit
 * pouvoir se prononcer AVANT que la base ne s'ouvre, sans quoi il y aurait un
 * instant où l'application est montée et les données lues alors que rien n'a
 * été saisi.
 *
 * `localStorage` et non `sessionStorage` : une PWA sur téléphone se ferme et
 * se rouvre sans arrêt, et redemander le mot de passe à chaque bascule
 * d'application rendrait l'outil pénible au point qu'on cesserait de s'en
 * servir. La session expire à minuit, ce qui est la garantie qui compte.
 *
 * Voir `src/verrou.js` pour ce que ce verrou protège et, surtout, ce qu'il ne
 * protège pas.
 */
export default function Verrou({ children }) {
  const [ouvert, setOuvert] = useState(() => {
    try {
      return sessionValide(JSON.parse(localStorage.getItem(CLE) || 'null'));
    } catch {
      return false;
    }
  });
  const [saisie, setSaisie] = useState('');
  const [essais, setEssais] = useState(0);
  const [attente, setAttente] = useState(0);

  // Le décompte rend l'attente visible. Sans lui, un champ qui refuse la
  // saisie sans rien dire passe pour une panne.
  useEffect(() => {
    if (attente <= 0) return undefined;
    const t = setTimeout(() => setAttente((a) => a - 1), 1000);
    return () => clearTimeout(t);
  }, [attente]);

  // La session expire à minuit : on referme sans attendre un rechargement,
  // sinon un onglet resté ouvert traverserait la nuit.
  useEffect(() => {
    if (!ouvert) return undefined;
    const t = setInterval(() => {
      try {
        if (!sessionValide(JSON.parse(localStorage.getItem(CLE) || 'null'))) setOuvert(false);
      } catch { setOuvert(false); }
    }, 60000);
    return () => clearInterval(t);
  }, [ouvert]);

  if (ouvert) return children;

  const soumettre = (e) => {
    e.preventDefault();
    if (attente > 0) return;
    if (verifier(saisie)) {
      localStorage.setItem(CLE, JSON.stringify(ouvrirSession()));
      setOuvert(true);
      return;
    }
    const n = essais + 1;
    setEssais(n);
    setAttente(attenteSecondes(n));
    setSaisie('');
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--couleur-fond)', color: 'var(--couleur-encre)',
      display: 'grid', placeItems: 'center', padding: 'var(--espace-4)',
    }}>
      <form
        onSubmit={soumettre}
        className="carte"
        style={{ width: '100%', maxWidth: 380, display: 'grid', gap: 'var(--espace-3)' }}
      >
        <h1 style={{ margin: 0, fontFamily: 'var(--police-titre)', fontSize: '1.5rem', fontWeight: 700 }}>
          Corridor <span style={{ color: 'var(--couleur-emphase)' }}>FX</span>
        </h1>

        <label style={{ display: 'block' }}>
          <span className="etiquette">Mot de passe</span>
          <input
            type="password"
            autoFocus
            autoComplete="off"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            disabled={attente > 0}
            aria-describedby="verrou-aide"
            className="champ"
            style={{ marginTop: 'var(--espace-1)' }}
          />
        </label>

        <button
          type="submit"
          disabled={attente > 0 || !saisie.trim()}
          style={{
            padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
            background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
          }}
        >
          {attente > 0 ? `Patientez ${attente} s` : 'Ouvrir'}
        </button>

        <p
          id="verrou-aide"
          role={essais > 0 ? 'alert' : undefined}
          style={{ margin: 0, fontSize: '0.85rem', color: essais > 0 ? 'var(--couleur-alerte)' : 'var(--couleur-encre-douce)' }}
        >
          {essais === 0
            ? 'Le mot de passe change chaque jour.'
            : essais < ESSAIS_AVANT_ATTENTE
              ? 'Mot de passe incorrect.'
              : 'Mot de passe incorrect. Vérifiez la date du jour.'}
        </p>
      </form>
    </div>
  );
}
